import type { MedplumClient } from '@medplum/core';
import type { Patient, Task } from '@medplum/fhirtypes';

function formatPatientName(patient: Patient): string | undefined {
  const n = patient.name?.[0];
  if (!n) return undefined;
  const label = [n.given?.join(' '), n.family].filter(Boolean).join(' ').trim();
  return label || undefined;
}

/** Fill `Task.for.display` from Patient.name when missing (for provider task titles). */
export async function enrichTasksWithPatientDisplay(
  medplum: MedplumClient,
  tasks: Task[],
): Promise<Task[]> {
  const patientIds = [
    ...new Set(
      tasks
        .filter((t) => t.for?.reference?.startsWith('Patient/') && !t.for?.display?.trim())
        .map((t) => t.for!.reference!.slice('Patient/'.length)),
    ),
  ];

  if (patientIds.length === 0) return tasks;

  let patients: Patient[] = [];
  try {
    patients = await medplum.searchResources('Patient', {
      _id: patientIds.join(','),
      _count: String(patientIds.length),
    });
  } catch {
    return tasks;
  }

  const nameById = new Map<string, string>();
  for (const patient of patients) {
    if (!patient.id) continue;
    const name = formatPatientName(patient);
    if (name) nameById.set(patient.id, name);
  }

  return tasks.map((task) => {
    const ref = task.for?.reference;
    if (!ref?.startsWith('Patient/') || task.for?.display?.trim()) return task;
    const display = nameById.get(ref.slice('Patient/'.length));
    if (!display) return task;
    return { ...task, for: { ...task.for, display } };
  });
}
